<template>
  <p class="text-base font-light">{{ $t('settings.appsModulesDesc') }}</p>
  <section class="max-w-3xl space-y-4">
    <div v-if="!isLoading">
      <div v-for="group in groupedModules" :key="group.app">
        <div
          class="flex items-center justify-between w-full px-4 py-3 border-b-2 bg-theme-50 border-theme-400">
          <h3 class="flex-1 pl-20 text-lg font-medium text-center text-theme-700">
            {{ group.app }}
          </h3>

          <BaseButton
            outline
            size="xs"
            color="light"
            @click="onAddModule(group)"
            class="flex items-center justify-center gap-2 ml-auto bg-white shadow-sm"
            ><IconAddSmall class="size-2" /> Add Module</BaseButton
          >
        </div>

        <draggable
          tag="ul"
          v-model="group.modules"
          group="apps"
          chosen-class="chosen"
          animation="300"
          handle=".handle"
          item-key="id">
          <template #item="{ element }">
            <li
              class="flex items-center justify-start gap-2 px-3 py-3 border-b first-of-type:border-t"
              @dragend="onDropModule()">
              <div class="h-full py-1 text-gray-400 rounded-sm cursor-grab bg-theme-50 handle">
                <IconDragVerticalSolid class="size-4" />
              </div>
              <BaseToggle
                :name="element.moduleName"
                :label="element.displayName"
                light
                v-model="element.isEnabled"
                @update:model-value="onUpdateToggle" />
              <button
                type="button"
                class="p-2 rounded-full hover:bg-red-50"
                @click="onDeleteAppModule(element)">
                <IconTrashOutline class="text-gray-300 hover:text-red-500 size-4" />
              </button>
            </li>
          </template>
        </draggable>
      </div>
    </div>
    <InstallationModalAddModule
      v-if="isModalAddModuleOpen"
      :modules="modalModules"
      @close="isModalAddModuleOpen = false"
      @save="onCreateModuleInstallation" />

    <BaseModalConfirm
      v-if="isDeleteConfirmActive"
      @cancel="onCancelDelete"
      @confirm="onConfirmDelete">
      <template #text>
        {{ $t('messages.info.deleteSingle', { 'item': selectedForDelete?.displayName }) }}
      </template>
    </BaseModalConfirm>
  </section>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { updateInstallation } from '@/services/api/mw';
import { storeToRefs } from 'pinia';
import { useLookupsStore } from '@/stores/lookups';
import { useToastStore } from '@/stores/toast';
import { useI18n } from 'vue-i18n';
import draggable from 'vuedraggable';

import InstallationModalAddModule from '@/components/settings/InstallationModalAddModule.vue';
import BaseButton from '@/components/common/BaseButton.vue';
import BaseToggle from '@/components/common/BaseToggle.vue';
import BaseModalConfirm from '@/components/common/BaseModalConfirm.vue';
import IconAddSmall from '@/assets/icons/IconAddSmall.vue';
import IconDragVerticalSolid from '../../assets/icons/IconDragVerticalSolid.vue';
import IconTrashOutline from '@/assets/icons/IconTrashOutline.vue';
import { type MWModule, type ModuleInstallation } from '@/types/MW';

type GroupedModule = { modules: ModuleInstallation[]; app: string; appId: string };

const groupedModules = ref<GroupedModule[]>([]);
const isLoading = ref(true);
const { mwApplications, mwModules, moduleInstallations } = storeToRefs(useLookupsStore());
const { getModuleInstalationsLookup, reloadSingleLookup } = useLookupsStore();

onMounted(async () => {
  await getModuleInstalationsLookup();
});

watch(
  [moduleInstallations, mwApplications, mwModules],
  () => {
    const appsReady = mwApplications.value?.length > 0;
    const modsReady = mwModules.value?.length > 0;
    if (appsReady && modsReady) {
      groupedModules.value = createGroupedModules(moduleInstallations.value || []);
      isLoading.value = false;
    }
  },
  { immediate: true }
);

function createGroupedModules(installation: ModuleInstallation[]) {
  return mwApplications.value.map((app) => ({
    app: app.name,
    appId: app.id,
    modules: installation
      ?.filter((module) => module.applicationName === app.name)
      .sort((a, b) => (a.order ?? Number.POSITIVE_INFINITY) - (b.order ?? Number.POSITIVE_INFINITY))
      .map(({ moduleName, isEnabled, mwApplicationId, mwModuleId, applicationName, order }) => ({
        moduleName,
        isEnabled,
        mwApplicationId,
        mwModuleId,
        applicationName,
        displayName: mwModules.value.find((x) => x.id == mwModuleId)?.displayName,
        order: order ?? 0,
      })),
  }));
}

const isModalAddModuleOpen = ref(false);

const modalModules = computed<MWModule[]>(() => {
  return mwModules.value.map((module) => {
    const enabledModule = moduleInstallations.value.find((x) => x.mwModuleId == module.id);
    return {
      appName: enabledModule ? enabledModule.applicationName : null,
      ...module,
    };
  });
});

function onDropModule() {
  onSave();
}

const selectedGroup = ref<GroupedModule>();

function onAddModule(group: GroupedModule) {
  isModalAddModuleOpen.value = true;
  selectedGroup.value = group;
}

function onCreateModuleInstallation(modules: MWModule[]) {
  modules.forEach((module) => {
    if (!selectedGroup.value) return;
    const newModuleInstallation: ModuleInstallation = {
      moduleName: module.name,
      applicationName: selectedGroup.value?.app,
      isEnabled: false,
      mwApplicationId: selectedGroup.value.appId,
      mwModuleId: module.id,
    };

    moduleInstallations.value.push(newModuleInstallation);
    groupedModules.value = createGroupedModules(moduleInstallations.value);
  });
  isModalAddModuleOpen.value = false;
  onSave();
}

function onUpdateToggle() {
  onSave();
}

const toastStore = useToastStore();
const { t: $t } = useI18n({ useScope: 'global' });

async function onSave() {
  groupedModules.value.forEach((group) => {
    group.modules.forEach((module) => {
      if (module.applicationName !== group.app) {
        module.applicationName = group.app;
        module.mwApplicationId = group.appId;
      }
    });
  });
  const modifiedModuleInstallations = groupedModules.value.flatMap((group: GroupedModule) =>
    group.modules.map((item: ModuleInstallation, idx) => ({
      applicationName: group.app,
      order: idx + 1,
      isEnabled: item.isEnabled,
      moduleName: item.moduleName,
      mwApplicationId: item.mwApplicationId,
      mwModuleId: item.mwModuleId,
    }))
  );

  const res = await updateInstallation(modifiedModuleInstallations);
  if (res) {
    await reloadSingleLookup('moduleInstallations');
    toastStore.success({ text: `${$t('common.toast.success')}` });
  } else toastStore.error({ text: `${$t('common.toast.error')}` });
}

const isDeleteConfirmActive = ref(false);
const selectedForDelete = ref<ModuleInstallation | null>();
function onDeleteAppModule(element: ModuleInstallation) {
  isDeleteConfirmActive.value = true;
  selectedForDelete.value = element;
}

function onCancelDelete() {
  isDeleteConfirmActive.value = false;
  selectedForDelete.value = null;
}

function onConfirmDelete() {
  groupedModules.value.forEach((group) => {
    const indexToDelete = group.modules.findIndex(
      (mod) => mod.mwModuleId == selectedForDelete.value?.mwModuleId
    );
    if (indexToDelete !== -1) group.modules.splice(indexToDelete, 1);

    const moduleInstallationsIdxToDelete = moduleInstallations.value.findIndex(
      (mod) => mod.mwModuleId == selectedForDelete.value?.mwModuleId
    );

    if (moduleInstallationsIdxToDelete !== -1)
      moduleInstallations.value.splice(moduleInstallationsIdxToDelete, 1);
  });

  isDeleteConfirmActive.value = false;
  onSave();
}
</script>
